/**
 * Chytrá analýza nemovitosti - porovnání zadaných dat vs RUIAN + kvalita odhadu
 */

export interface PropertyData {
  propertyType?: string; // flat, house, land
  houseType?: string; // family_house, cottage, villa, hut, apartment_building, other
  area?: number; // zadaná plocha m² (floorArea)
  landArea?: number; // plocha pozemku m² (lotArea)
  condition?: string; // stav (rating)
  localType?: string; // typ lokality
}

export interface RuianData {
  adresniMisto?: {
    kod?: string;
    adresa?: string;
    cisloDomovni?: string;
    cisloOrientacni?: string;
    psc?: string;
  };
  stavebniObjekt?: {
    kod?: string;
    pocetPodlazi?: number;
    pocetBytu?: number;
    podlahovaPlocha?: number;
    obestavenyprostor?: number;
    druhKonstrukce?: string;
    dokonceni?: string;
    zpusobVyuziti?: string;
    typStavebnihoObjektu?: string;
  };
  parcela?: {
    vymera?: number;
    druhPozemku?: string;
    kmenoveCislo?: number;
    nazevKatastralnihoUzemi?: string;
  };
}

export interface ValuationData {
  avgScore?: number;
  keepIdsCount?: number;
  distance?: number;
  avgDistance?: number;
  avgAge?: number;
  calcArea?: number;
}

export type InsightSeverity = 'info' | 'warning' | 'error' | 'success';

export interface PropertyInsight {
  id: string;
  severity: InsightSeverity;
  title: string;
  description: string;
  suggestion?: string;
  data?: Record<string, string | number>;
}

/**
 * Vypočítá stáří budovy z data dokončení
 */
function calculateBuildingAge(dokonceni?: string): number | null {
  if (!dokonceni) return null;
  
  // Formát může být "31.12.1919" nebo "1919"
  const match = dokonceni.match(/(\d{4})/);
  if (!match || !match[1]) return null;
  
  const year = parseInt(match[1], 10);
  const currentYear = new Date().getFullYear();
  return currentYear - year;
}

/**
 * Mapuje RUIAN druh konstrukce na náš typ
 */
function mapConstructionType(ruianKonstrukce?: string): string | null {
  if (!ruianKonstrukce) return null;
  
  const lower = ruianKonstrukce.toLowerCase();
  if (lower.includes('cihla') || lower.includes('tvárnice')) return 'brick';
  if (lower.includes('panel')) return 'panel';
  if (lower.includes('dřev')) return 'wood';
  if (lower.includes('kámen')) return 'stone';
  if (lower.includes('montovan')) return 'montage';
  return 'other';
}

/**
 * Odhadne průměrnou plochu bytu v budově
 */
function estimateApartmentArea(stavebniObjekt?: RuianData['stavebniObjekt']): number | null {
  if (!stavebniObjekt?.pocetBytu || stavebniObjekt.pocetBytu === 0) return null;
  
  // Pokud máme podlahovou plochu, použijeme ji
  if (stavebniObjekt.podlahovaPlocha) {
    return Math.round(stavebniObjekt.podlahovaPlocha / stavebniObjekt.pocetBytu);
  }
  
  // Jinak odhadneme z obestavěného prostoru (průměrná výška 2.8m, 80% využití)
  if (stavebniObjekt.obestavenyprostor) {
    const estimatedFloorArea = (stavebniObjekt.obestavenyprostor / 2.8) * 0.8;
    return Math.round(estimatedFloorArea / stavebniObjekt.pocetBytu);
  }
  
  return null;
}

/**
 * Hlavní analytická funkce - generuje insights
 */
export function analyzeProperty(
  property: PropertyData,
  ruian?: RuianData,
  valuation?: ValuationData
): PropertyInsight[] {
  const insights: PropertyInsight[] = [];
  
  // ========== ANALÝZA STÁŘÍ BUDOVY ==========
  const buildingAge = calculateBuildingAge(ruian?.stavebniObjekt?.dokonceni);
  const buildingYear = ruian?.stavebniObjekt?.dokonceni?.match(/(\d{4})/)?.[1];
  
  if (buildingAge !== null && buildingYear) {
    let ageSeverity: InsightSeverity = 'info';
    let ageDescription = '';
    let ageSuggestion = '';
    
    // Výpočet odhadované životnosti a zůstatkové hodnoty
    const expectedLifespan = 100; // průměrná životnost budovy
    const remainingLifespan = Math.max(0, expectedLifespan - buildingAge);
    const lifespanPercent = Math.round((remainingLifespan / expectedLifespan) * 100);
    
    if (buildingAge > 100) {
      ageSeverity = 'warning';
      ageDescription = `Historická budova z roku ${buildingYear}. Stáří ${buildingAge} let přesahuje běžnou životnost. Hodnota závisí na provedených rekonstrukcích.`;
      ageSuggestion = 'Prověřte stav nosných konstrukcí, rozvodů a střechy. Zjistěte historii rekonstrukcí.';
    } else if (buildingAge > 50) {
      ageSeverity = 'info';
      ageDescription = `Budova z roku ${buildingYear} (${buildingAge} let). Zbývající teoretická životnost: ${remainingLifespan} let (${lifespanPercent}%). Pravděpodobně vyžaduje nebo prošla modernizací.`;
      ageSuggestion = 'Ověřte stav rozvodů, zateplení a případné rekonstrukce.';
    } else if (buildingAge > 20) {
      ageSeverity = 'success';
      ageDescription = `Budova z roku ${buildingYear} (${buildingAge} let). Zbývající teoretická životnost: ${remainingLifespan} let (${lifespanPercent}%). Standardní stáří bez výrazných rizik.`;
    } else if (buildingAge <= 5) {
      ageSeverity = 'success';
      ageDescription = `Novostavba z roku ${buildingYear} (${buildingAge} let). Moderní standardy, minimální riziko skrytých vad. Plná zbývající životnost.`;
    } else {
      ageSeverity = 'success';
      ageDescription = `Relativně nová budova z roku ${buildingYear} (${buildingAge} let). Zbývající teoretická životnost: ${remainingLifespan} let (${lifespanPercent}%).`;
    }
    
    insights.push({
      id: 'building-age',
      severity: ageSeverity,
      title: `Stáří budovy: ${buildingAge} let (rok ${buildingYear})`,
      description: ageDescription,
      suggestion: ageSuggestion || undefined,
      data: {
        'Rok dokončení': buildingYear,
        'Stáří': `${buildingAge} let`,
        'Zbývající životnost': `${remainingLifespan} let (${lifespanPercent}%)`,
      },
    });
  }
  
  // ========== ANALÝZA TYPU NEMOVITOSTI VS RUIAN ==========
  const pocetBytu = ruian?.stavebniObjekt?.pocetBytu;
  const pocetPodlazi = ruian?.stavebniObjekt?.pocetPodlazi;
  const zpusobVyuziti = ruian?.stavebniObjekt?.zpusobVyuziti?.toLowerCase() || '';
  const podlahovaPlocha = ruian?.stavebniObjekt?.podlahovaPlocha;
  const obestavenyprostor = ruian?.stavebniObjekt?.obestavenyprostor;
  
  // Pozitivní info o budově - vždy když máme data
  if (pocetPodlazi || pocetBytu || podlahovaPlocha || obestavenyprostor) {
    const buildingData: Record<string, string | number> = {};
    if (pocetPodlazi) buildingData['Počet podlaží'] = pocetPodlazi;
    if (pocetBytu) buildingData['Počet bytů'] = pocetBytu;
    if (podlahovaPlocha) buildingData['Celková podlahová plocha'] = `${podlahovaPlocha.toLocaleString('cs-CZ')} m²`;
    if (obestavenyprostor) buildingData['Obestavěný prostor'] = `${obestavenyprostor.toLocaleString('cs-CZ')} m³`;
    
    // Výpočet průměrné plochy na podlaží
    if (podlahovaPlocha && pocetPodlazi) {
      const avgFloorArea = Math.round(podlahovaPlocha / pocetPodlazi);
      buildingData['Prům. plocha podlaží'] = `${avgFloorArea} m²`;
    }
    
    // Výpočet průměrné plochy bytu
    if (podlahovaPlocha && pocetBytu && pocetBytu > 0) {
      const avgApartmentArea = Math.round(podlahovaPlocha / pocetBytu);
      buildingData['Prům. plocha bytu'] = `${avgApartmentArea} m²`;
    }
    
    insights.push({
      id: 'building-info',
      severity: 'info',
      title: 'Parametry budovy z RUIAN',
      description: `Budova má ${pocetPodlazi || '?'} podlaží${pocetBytu ? ` a ${pocetBytu} bytových jednotek` : ''}.${podlahovaPlocha ? ` Celková podlahová plocha: ${podlahovaPlocha.toLocaleString('cs-CZ')} m².` : ''}`,
      data: buildingData,
    });
  }
  
  if (property.propertyType === 'flat' && pocetBytu !== undefined) {
    // Oceňujeme BYT - můžeme odhadnout jeho parametry z budovy
    const estimatedArea = estimateApartmentArea(ruian?.stavebniObjekt);
    
    // Výpočet podílu na společných částech
    if (property.area && podlahovaPlocha && pocetBytu > 0) {
      const sharePercent = ((property.area / podlahovaPlocha) * 100).toFixed(2);
      const estimatedCommonArea = Math.round((podlahovaPlocha - (pocetBytu * (estimatedArea || property.area))) / pocetBytu);
      
      insights.push({
        id: 'flat-share',
        severity: 'success',
        title: `Podíl bytu na budově: ${sharePercent}%`,
        description: `Byt o ploše ${property.area} m² představuje ${sharePercent}% z celkové plochy budovy (${podlahovaPlocha.toLocaleString('cs-CZ')} m²).`,
        data: {
          'Plocha bytu': `${property.area} m²`,
          'Celková plocha budovy': `${podlahovaPlocha.toLocaleString('cs-CZ')} m²`,
          'Podíl': `${sharePercent}%`,
          ...(estimatedCommonArea > 0 ? { 'Odhadovaný podíl na spol. částech': `${estimatedCommonArea} m²` } : {}),
        },
      });
    }
    
    // Kontrola zadané plochy vs odhad
    if (estimatedArea && property.area) {
      const diff = Math.abs(property.area - estimatedArea);
      const diffPercent = (diff / estimatedArea) * 100;
      
      if (diffPercent > 50) {
        insights.push({
          id: 'flat-area-mismatch',
          severity: 'warning',
          title: 'Neobvyklá plocha bytu',
          description: `Zadaná plocha ${property.area} m² se výrazně liší od průměrné plochy bytu v domě (${estimatedArea} m²).`,
          suggestion: property.area > estimatedArea * 1.5 
            ? 'Může jít o mezonet nebo spojené byty. Ověřte správnost zadané plochy.'
            : 'Může jít o garsonku nebo menší byt. Ověřte správnost zadané plochy.',
        });
      } else {
        insights.push({
          id: 'flat-area-ok',
          severity: 'success',
          title: 'Plocha bytu odpovídá průměru',
          description: `Zadaná plocha ${property.area} m² je v souladu s průměrnou plochou bytu v domě (${estimatedArea} m²). Odchylka: ${diffPercent.toFixed(0)}%.`,
          data: {
            'Zadaná plocha': `${property.area} m²`,
            'Průměrná plocha bytu': `${estimatedArea} m²`,
            'Odchylka': `${diffPercent.toFixed(0)}%`,
          },
        });
      }
    }
  }
  
  if (property.propertyType === 'house') {
    // Oceňujeme DŮM
    if (pocetBytu && pocetBytu > 2) {
      // Více bytů = pravděpodobně činžovní dům
      // Výpočet potenciálního výnosu
      const avgRentPerM2 = 250; // průměrný nájem Kč/m²/měsíc
      const estimatedMonthlyRent = podlahovaPlocha ? Math.round(podlahovaPlocha * avgRentPerM2 * 0.7) : null; // 70% využití
      const estimatedYearlyRent = estimatedMonthlyRent ? estimatedMonthlyRent * 12 : null;
      
      insights.push({
        id: 'house-is-apartment-building',
        severity: 'warning',
        title: `Budova obsahuje ${pocetBytu} bytových jednotek`,
        description: `Zadali jste typ "Dům", ale RUIAN eviduje ${pocetBytu} bytů. Může jít o činžovní dům nebo bytový dům.`,
        suggestion: 'Zvažte, zda oceňujete celou budovu nebo jen její část. Pro činžovní domy platí jiná metodika ocenění (výnosová metoda).',
        data: {
          'Počet bytů': pocetBytu,
          'Způsob využití (RUIAN)': ruian?.stavebniObjekt?.zpusobVyuziti || '-',
          ...(estimatedMonthlyRent ? { 'Odhadovaný měsíční výnos': `${estimatedMonthlyRent.toLocaleString('cs-CZ')} Kč` } : {}),
          ...(estimatedYearlyRent ? { 'Odhadovaný roční výnos': `${estimatedYearlyRent.toLocaleString('cs-CZ')} Kč` } : {}),
        },
      });
    } else if (zpusobVyuziti.includes('rodinný')) {
      insights.push({
        id: 'house-confirmed-rd',
        severity: 'success',
        title: 'Potvrzeno: Rodinný dům',
        description: 'RUIAN potvrzuje, že jde o rodinný dům. Typ nemovitosti odpovídá evidenci.',
        data: {
          'Způsob využití': ruian?.stavebniObjekt?.zpusobVyuziti || '-',
          ...(pocetPodlazi ? { 'Počet podlaží': pocetPodlazi } : {}),
        },
      });
    }
    
    // Výpočet zastavěné plochy vs pozemek
    if (property.landArea && ruian?.parcela?.vymera) {
      const zastavenaPlocha = podlahovaPlocha && pocetPodlazi ? Math.round(podlahovaPlocha / pocetPodlazi) : null;
      if (zastavenaPlocha) {
        const zastaveniPercent = ((zastavenaPlocha / ruian.parcela.vymera) * 100).toFixed(0);
        const volnaPlocha = ruian.parcela.vymera - zastavenaPlocha;
        
        insights.push({
          id: 'land-utilization',
          severity: 'info',
          title: `Zastavěnost pozemku: ${zastaveniPercent}%`,
          description: `Budova zastavuje přibližně ${zastavenaPlocha} m² z celkové plochy pozemku ${ruian.parcela.vymera} m². Volná plocha: ${volnaPlocha} m².`,
          data: {
            'Zastavěná plocha': `${zastavenaPlocha} m²`,
            'Plocha pozemku': `${ruian.parcela.vymera} m²`,
            'Zastavěnost': `${zastaveniPercent}%`,
            'Volná plocha': `${volnaPlocha} m²`,
          },
        });
      }
    }
  }
  
  // ========== ANALÝZA POZEMKU ==========
  if (ruian?.parcela?.vymera && property.landArea) {
    const diff = Math.abs(ruian.parcela.vymera - property.landArea);
    const diffPercent = (diff / ruian.parcela.vymera) * 100;
    
    if (diffPercent > 20) {
      insights.push({
        id: 'land-area-mismatch',
        severity: 'warning',
        title: 'Nesoulad plochy pozemku',
        description: `Zadaná plocha pozemku (${property.landArea} m²) se liší od RUIAN (${ruian.parcela.vymera} m²).`,
        suggestion: 'Ověřte, zda nemovitost stojí na více parcelách nebo zda je zadaná plocha správná.',
        data: {
          'Zadaná plocha': `${property.landArea} m²`,
          'RUIAN parcela': `${ruian.parcela.vymera} m²`,
          'Rozdíl': `${diff} m² (${diffPercent.toFixed(0)}%)`,
        },
      });
    }
  }
  
  if (property.propertyType === 'land' && ruian?.parcela) {
    // Oceňujeme POZEMEK
    const druhPozemku = ruian.parcela.druhPozemku?.toLowerCase() || '';
    
    if (druhPozemku.includes('zastavěná')) {
      insights.push({
        id: 'land-has-building',
        severity: 'warning',
        title: 'Na pozemku stojí stavba',
        description: `Pozemek je evidován jako "${ruian.parcela.druhPozemku}". Oceňujete pouze pozemek, nebo i stavbu?`,
        suggestion: 'Pokud oceňujete pouze pozemek, cena může být ovlivněna existující stavbou.',
      });
    } else if (druhPozemku.includes('orná') || druhPozemku.includes('trvalý travní')) {
      insights.push({
        id: 'land-agricultural',
        severity: 'info',
        title: 'Zemědělský pozemek',
        description: `Pozemek je evidován jako "${ruian.parcela.druhPozemku}".`,
        suggestion: 'Pro stavební účely může být nutná změna územního plánu.',
      });
    }
  }
  
  // ========== ANALÝZA KONSTRUKCE ==========
  if (ruian?.stavebniObjekt?.druhKonstrukce) {
    const ruianKonstrukce = ruian.stavebniObjekt.druhKonstrukce;
    
    // Pozitivní info o konstrukci
    insights.push({
      id: 'construction-type',
      severity: 'info',
      title: `Konstrukce: ${ruianKonstrukce}`,
      description: `Budova je postavena z materiálu: ${ruianKonstrukce}.`,
      data: {
        'Typ konstrukce': ruianKonstrukce,
      },
    });
    
    // Specifické poznámky k typům konstrukce
    if (ruianKonstrukce.toLowerCase().includes('panel') && buildingAge && buildingAge > 40) {
      insights.push({
        id: 'panel-old',
        severity: 'warning',
        title: 'Starší panelový dům',
        description: `Panelová konstrukce stará ${buildingAge} let. Může vyžadovat revitalizaci.`,
        suggestion: 'Zjistěte, zda proběhla revitalizace (zateplení, výměna oken, oprava rozvodů).',
      });
    }
    
    // Pozitivní info pro cihlové/kamenné budovy
    if ((ruianKonstrukce.toLowerCase().includes('cihla') || ruianKonstrukce.toLowerCase().includes('kámen')) && buildingAge && buildingAge > 50) {
      insights.push({
        id: 'solid-construction',
        severity: 'success',
        title: 'Solidní tradiční konstrukce',
        description: `Cihlová/kamenná konstrukce má obvykle dlouhou životnost a dobré tepelně-izolační vlastnosti.`,
      });
    }
  }
  
  // ========== ANALÝZA KVALITY ODHADU VALUO ==========
  if (valuation) {
    // Skóre podobnosti
    if (valuation.avgScore !== undefined) {
      let scoreSeverity: InsightSeverity = 'success';
      let scoreDescription = '';
      
      if (valuation.avgScore < 0.3) {
        scoreSeverity = 'error';
        scoreDescription = 'Velmi nízké skóre podobnosti. Odhad je málo spolehlivý - v okolí nejsou dostatečně podobné nemovitosti.';
      } else if (valuation.avgScore < 0.5) {
        scoreSeverity = 'warning';
        scoreDescription = 'Nízké skóre podobnosti. Odhad může být méně přesný.';
      } else if (valuation.avgScore < 0.7) {
        scoreSeverity = 'info';
        scoreDescription = 'Průměrné skóre podobnosti. Odhad je orientační.';
      } else {
        scoreDescription = 'Vysoké skóre podobnosti. Odhad je založen na dobře srovnatelných nemovitostech.';
      }
      
      insights.push({
        id: 'valuation-score',
        severity: scoreSeverity,
        title: `Kvalita odhadu: ${(valuation.avgScore * 100).toFixed(0)}%`,
        description: scoreDescription,
        data: {
          'Skóre podobnosti': valuation.avgScore.toFixed(3),
        },
      });
    }
    
    // Počet použitých inzerátů
    if (valuation.keepIdsCount !== undefined) {
      let countSeverity: InsightSeverity = 'info';
      let countDescription = '';
      
      if (valuation.keepIdsCount < 3) {
        countSeverity = 'error';
        countDescription = `Odhad je založen pouze na ${valuation.keepIdsCount} inzerátech. To je velmi málo pro spolehlivý odhad.`;
      } else if (valuation.keepIdsCount < 5) {
        countSeverity = 'warning';
        countDescription = `Odhad je založen na ${valuation.keepIdsCount} inzerátech. Doporučeno je alespoň 5.`;
      } else if (valuation.keepIdsCount < 10) {
        countSeverity = 'info';
        countDescription = `Odhad je založen na ${valuation.keepIdsCount} inzerátech.`;
      } else {
        countSeverity = 'success';
        countDescription = `Odhad je založen na ${valuation.keepIdsCount} inzerátech. Dobrý vzorek dat.`;
      }
      
      insights.push({
        id: 'valuation-sample-size',
        severity: countSeverity,
        title: `Použito ${valuation.keepIdsCount} srovnávacích inzerátů`,
        description: countDescription,
      });
    }
    
    // Vzdálenost hledání
    if (valuation.avgDistance && valuation.avgDistance > 2000) {
      insights.push({
        id: 'valuation-distance',
        severity: 'warning',
        title: 'Velká vzdálenost srovnávacích nemovitostí',
        description: `Průměrná vzdálenost srovnávacích nemovitostí je ${Math.round(valuation.avgDistance)} m. Ceny v jiných lokalitách se mohou lišit.`,
        suggestion: 'V dané lokalitě je málo srovnatelných nemovitostí. Odhad může být méně přesný.',
        data: {
          'Prům. vzdálenost': `${Math.round(valuation.avgDistance)} m`,
          'Max. radius': valuation.distance ? `${valuation.distance} m` : '-',
        },
      });
    }
    
    // Stáří dat
    if (valuation.avgAge && valuation.avgAge > 90) {
      insights.push({
        id: 'valuation-data-age',
        severity: 'warning',
        title: 'Starší srovnávací data',
        description: `Průměrné stáří použitých inzerátů je ${Math.round(valuation.avgAge)} dní. Trh se mohl změnit.`,
        suggestion: 'Aktuální tržní ceny mohou být odlišné od historických dat.',
      });
    }
  }
  
  // ========== CELKOVÉ HODNOCENÍ ==========
  const errorCount = insights.filter(i => i.severity === 'error').length;
  const warningCount = insights.filter(i => i.severity === 'warning').length;
  
  if (errorCount > 0 || warningCount > 2) {
    insights.unshift({
      id: 'overall-caution',
      severity: 'warning',
      title: 'Doporučujeme zvýšenou opatrnost',
      description: `Analýza odhalila ${errorCount} závažných a ${warningCount} méně závažných upozornění. Odhad ceny může být méně spolehlivý.`,
    });
  } else if (warningCount === 0 && insights.some(i => i.severity === 'success')) {
    insights.unshift({
      id: 'overall-good',
      severity: 'success',
      title: 'Data vypadají konzistentně',
      description: 'Zadaná data odpovídají RUIAN evidenci a odhad je založen na kvalitních datech.',
    });
  }
  
  return insights;
}

/**
 * Formátuje severity pro zobrazení
 */
export function getSeverityColor(severity: InsightSeverity): string {
  switch (severity) {
    case 'error': return 'red';
    case 'warning': return 'yellow';
    case 'success': return 'green';
    default: return 'blue';
  }
}

export function getSeverityBgClass(severity: InsightSeverity): string {
  switch (severity) {
    case 'error': return 'bg-red-50 border-red-200';
    case 'warning': return 'bg-yellow-50 border-yellow-200';
    case 'success': return 'bg-green-50 border-green-200';
    default: return 'bg-blue-50 border-blue-200';
  }
}

export function getSeverityTextClass(severity: InsightSeverity): string {
  switch (severity) {
    case 'error': return 'text-red-800';
    case 'warning': return 'text-yellow-800';
    case 'success': return 'text-green-800';
    default: return 'text-blue-800';
  }
}

export function getSeverityIconClass(severity: InsightSeverity): string {
  switch (severity) {
    case 'error': return 'text-red-500';
    case 'warning': return 'text-yellow-500';
    case 'success': return 'text-green-500';
    default: return 'text-blue-500';
  }
}
